/**
 * SummaryReportServiceArcGIS
 *
 * Feature Service implementation of SummaryReportServiceInterface, against layer 31
 * (SummaryReport) and layer 32 (UserSummaryReportRelationship). Not yet wired — see
 * SummaryReportService for the implementation currently exported.
 */

import esriRequest from '@arcgis/core/request';
import * as query from '@arcgis/core/rest/query';
import Query from '@arcgis/core/rest/support/Query';

import { getAppConfig, getAttributeServiceUrl } from '../utils/ReduxHelper';
import FieldNames from '../defaults/FieldNames.json';
import type {
    SummaryReport,
    SummaryReportChartState,
    SummaryReportFilterState,
    SummaryReportGridState,
    SummaryReportPivotState,
} from '../components/SummaryReports/types';
import type { SummaryReportServiceInterface } from './SummaryReportService';

const REPORT = FieldNames.summaryReports;
const SHARE = FieldNames.userSummaryReportRelationship;

/** The recipient column is schema-qualified, so the dots are quoted in every where clause. */
const SHARE_RECIPIENT_SQL = `"${SHARE.sharedWithUserID}"`;

/** A where clause has a length ceiling, so IN lists are issued in batches of this size. */
const IN_CLAUSE_BATCH = 100;

// ─── URLs ────────────────────────────────────────────────────────────────────

function layerUrl(configKey: 'summaryReports' | 'userSummaryReportRelationship'): string {
    const config = getAppConfig();
    const root = getAttributeServiceUrl();
    if (!root) throw new Error('Summary reports are unavailable — the service is still loading.');
    return `${root}/${config[configKey][config.env].layerId}`;
}

const reportsUrl = () => layerUrl('summaryReports');
const sharesUrl = () => layerUrl('userSummaryReportRelationship');

// ─── Value conversion ────────────────────────────────────────────────────────

/** ArcGIS stores GUIDs braced and upper case; the app carries them bare and lower. */
const bare = (guid: string): string => guid.replace(/[{}]/g, '').toLowerCase();
const braced = (guid: string): string => `{${bare(guid).toUpperCase()}}`;

const sqlText = (value: string): string => `'${value.replace(/'/g, "''")}'`;

/**
 * The service rejects `<` and `>` in text fields, and ArcGIS SQL is made of them (`>=`,
 * `<>`). Escaped on the way in, restored on the way out, so nothing above sees either form.
 */
const escapeAngles = (value: string): string =>
    value.replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const restoreAngles = (value: string): string =>
    value.replaceAll('&lt;', '<').replaceAll('&gt;', '>');

/** Date fields come back as epoch milliseconds; the app carries ISO-8601. */
const toIso = (epoch: unknown): string =>
    typeof epoch === 'number' ? new Date(epoch).toISOString() : '';
const toEpoch = (iso: string | undefined): number | null => (iso ? new Date(iso).getTime() : null);

/** The state blobs are opaque JSON. A corrupt one loads as absent rather than throwing. */
function parseBlob<T>(raw: unknown): T | undefined {
    if (typeof raw !== 'string' || !raw) return undefined;
    try {
        return JSON.parse(restoreAngles(raw)) as T;
    } catch {
        return undefined;
    }
}

const writeBlob = (value: unknown): string | null =>
    value === undefined ? null : escapeAngles(JSON.stringify(value));

// ─── Transport ───────────────────────────────────────────────────────────────

type Attributes = Record<string, unknown>;

async function selectAttributes(url: string, where: string): Promise<Attributes[]> {
    const results = await query.executeQueryJSON(
        url,
        new Query({ where, outFields: ['*'], returnGeometry: false })
    );
    return results?.features?.map((f) => f.attributes as Attributes) ?? [];
}

/** Runs one query per batch of ids and flattens the result. */
async function selectByIdBatches(
    url: string,
    field: string,
    ids: string[],
    extraClause?: string
): Promise<Attributes[]> {
    const batches: Attributes[][] = [];
    for (let i = 0; i < ids.length; i += IN_CLAUSE_BATCH) {
        const list = ids
            .slice(i, i + IN_CLAUSE_BATCH)
            .map((id) => sqlText(braced(id)))
            .join(',');
        const clause = `${field} IN (${list})`;
        batches.push(
            await selectAttributes(url, extraClause ? `(${clause}) OR (${extraClause})` : clause)
        );
    }
    return batches.flat();
}

/**
 * Feature Service edits answer 200 with the failure in the body, so both the request and
 * every per-feature result have to be checked.
 */
async function applyEdit(
    url: string,
    operation: 'addFeatures' | 'updateFeatures' | 'deleteFeatures',
    data: Record<string, string>
): Promise<Array<{ objectId?: number; globalId?: string }>> {
    // esriRequest signs the call from the identity manager, so no token is passed here.
    const { data: response } = await esriRequest(`${url}/${operation}`, {
        method: 'post',
        responseType: 'json',
        query: { f: 'json', ...data },
    });

    if (response?.error) throw new Error(response.error.message ?? `${operation} failed.`);

    const results: any[] =
        response?.addResults ?? response?.updateResults ?? response?.deleteResults ?? [];
    const failure = results.find((r) => r?.success === false);
    if (failure) throw new Error(failure.error?.description ?? `${operation} failed.`);
    return results;
}

// ─── Mapping ─────────────────────────────────────────────────────────────────

function toAttributes(report: SummaryReport, editor: string): Attributes {
    return {
        [REPORT.title]: escapeAngles(report.name),
        [REPORT.description]:
            report.description == null ? null : escapeAngles(report.description),
        [REPORT.createdBy]: report.createdBy,
        [REPORT.createdOnDate]: toEpoch(report.createdAt),
        [REPORT.lastModifiedBy]: editor,
        [REPORT.lastModifiedDate]: toEpoch(report.updatedAt),
        [REPORT.expression]: escapeAngles(report.definitionExpression),
        [REPORT.gridState]: writeBlob(report.gridState),
        [REPORT.pivotState]: writeBlob(report.pivotState),
        [REPORT.chartState]: writeBlob(report.chartState),
        [REPORT.filterState]: writeBlob(report.filterState),
    };
}

function toReport(attributes: Attributes, sharedWithUserIds: string[]): SummaryReport {
    return {
        id: bare(String(attributes[REPORT.globalID] ?? '')),
        name: restoreAngles(String(attributes[REPORT.title] ?? '')),
        description:
            attributes[REPORT.description] == null
                ? undefined
                : restoreAngles(String(attributes[REPORT.description])),
        createdBy: String(attributes[REPORT.createdBy] ?? ''),
        createdAt: toIso(attributes[REPORT.createdOnDate]),
        updatedAt: toIso(attributes[REPORT.lastModifiedDate]),
        // Derived: the share rows are the only record of who can see this.
        visibility: sharedWithUserIds.length ? 'shared' : 'private',
        sharedWithUserIds,
        sharedWithOrgIds: [],
        definitionExpression: restoreAngles(String(attributes[REPORT.expression] ?? '')),
        filterState: parseBlob<SummaryReportFilterState>(attributes[REPORT.filterState]),
        gridState: parseBlob<SummaryReportGridState>(attributes[REPORT.gridState]) ?? {},
        pivotState: parseBlob<SummaryReportPivotState>(attributes[REPORT.pivotState]),
        chartState: parseBlob<SummaryReportChartState>(attributes[REPORT.chartState]),
    };
}

// ─── Share rows ──────────────────────────────────────────────────────────────

async function recipientsOf(reportIds: string[]): Promise<Map<string, string[]>> {
    const byReport = new Map<string, string[]>(reportIds.map((id) => [id, []]));
    if (!reportIds.length) return byReport;

    const rows = await selectByIdBatches(sharesUrl(), SHARE.userSummaryReportsID, reportIds);
    for (const row of rows) {
        const reportId = bare(String(row[SHARE.userSummaryReportsID] ?? ''));
        const recipient = String(row[SHARE.sharedWithUserID] ?? '');
        if (recipient) byReport.get(reportId)?.push(recipient);
    }
    return byReport;
}

async function addShares(reportId: string, userIds: string[]): Promise<void> {
    if (!userIds.length) return;
    const features = userIds.map((userId) => ({
        attributes: {
            [SHARE.userSummaryReportsID]: braced(reportId),
            [SHARE.sharedWithUserID]: userId,
        },
    }));
    await applyEdit(sharesUrl(), 'addFeatures', { features: JSON.stringify(features) });
}

async function deleteShares(reportId: string, userIds?: string[]): Promise<void> {
    const scope = `${SHARE.userSummaryReportsID} = ${sqlText(braced(reportId))}`;
    if (userIds && !userIds.length) return;
    const where = userIds
        ? `(${scope}) AND (${SHARE_RECIPIENT_SQL} IN (${userIds.map(sqlText).join(',')}))`
        : scope;
    await applyEdit(sharesUrl(), 'deleteFeatures', { where });
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const SummaryReportServiceArcGIS: SummaryReportServiceInterface = {
    /**
     * The id is assigned by the service, so it is read off the write rather than sent with
     * it. GlobalID is not editable, so `report.id` is ignored here.
     */
    async saveSummaryReport(report: SummaryReport): Promise<SummaryReport> {
        const url = reportsUrl();
        const [result] = await applyEdit(url, 'addFeatures', {
            features: JSON.stringify([{ attributes: toAttributes(report, report.createdBy) }]),
            returnGlobalIds: 'true',
        });

        // Older services omit globalId from the write's result, so fall back to reading it.
        let globalId = result?.globalId;
        if (!globalId && result?.objectId != null) {
            const [row] = await selectAttributes(url, `${REPORT.objectID} = ${result.objectId}`);
            globalId = row?.[REPORT.globalID] as string | undefined;
        }
        if (!globalId) throw new Error('The report saved but the service returned no id for it.');

        const id = bare(globalId);
        await addShares(id, report.sharedWithUserIds);
        return {
            ...report,
            id,
            visibility: report.sharedWithUserIds.length ? 'shared' : 'private',
        };
    },

    async getSummaryReportById(id: string): Promise<SummaryReport | null> {
        const [row] = await selectAttributes(
            reportsUrl(),
            `${REPORT.globalID} = ${sqlText(braced(id))}`
        );
        if (!row) return null;
        const recipients = await recipientsOf([id]);
        return toReport(row, recipients.get(id) ?? []);
    },

    /**
     * Reports the user owns, plus those shared with them. Three queries: the shares naming
     * them, the reports those resolve to alongside their own, then the recipients of the
     * whole set for the "shared with N people" count.
     */
    async getAllSummaryReportsForUser(userId: string): Promise<SummaryReport[]> {
        const owned = `${REPORT.createdBy} = ${sqlText(userId)}`;

        const sharedWithMe = await selectAttributes(
            sharesUrl(),
            `${SHARE_RECIPIENT_SQL} = ${sqlText(userId)}`
        );
        const sharedIds = [
            ...new Set(
                sharedWithMe
                    .map((row) => bare(String(row[SHARE.userSummaryReportsID] ?? '')))
                    .filter(Boolean)
            ),
        ];

        const rows = sharedIds.length
            ? await selectByIdBatches(reportsUrl(), REPORT.globalID, sharedIds, owned)
            : await selectAttributes(reportsUrl(), owned);

        // Batched IN clauses each carry the owner clause, so the same report can come back twice.
        const unique = new Map<string, Attributes>();
        for (const row of rows) unique.set(bare(String(row[REPORT.globalID] ?? '')), row);

        const recipients = await recipientsOf([...unique.keys()]);
        return [...unique].map(([id, row]) => toReport(row, recipients.get(id) ?? []));
    },

    /** GlobalID is not editable, so the row is addressed by the OBJECTID it resolves to. */
    async updateSummaryReport(report: SummaryReport): Promise<SummaryReport> {
        const url = reportsUrl();
        const [row] = await selectAttributes(
            url,
            `${REPORT.globalID} = ${sqlText(braced(report.id))}`
        );
        if (!row) throw new Error('That report no longer exists.');

        await applyEdit(url, 'updateFeatures', {
            features: JSON.stringify([
                {
                    attributes: {
                        [REPORT.objectID]: row[REPORT.objectID],
                        ...toAttributes(report, report.createdBy),
                    },
                },
            ]),
        });

        const existing = (await recipientsOf([report.id])).get(report.id) ?? [];
        const wanted = report.sharedWithUserIds;
        await deleteShares(
            report.id,
            existing.filter((u) => !wanted.includes(u))
        );
        await addShares(
            report.id,
            wanted.filter((u) => !existing.includes(u))
        );
        return report;
    },

    /**
     * Children first: nothing cascades, and a share row outliving its report would show up
     * as a phantom entry in someone's list.
     */
    async deleteSummaryReport(id: string): Promise<void> {
        await deleteShares(id);
        await applyEdit(reportsUrl(), 'deleteFeatures', {
            where: `${REPORT.globalID} = ${sqlText(braced(id))}`,
        });
    },
};
